import { clearServiceWorkerAndCaches } from './clearServiceWorkerAndCaches';

/**
 * One-time cleanup when moving from @lcdp/offline-plugin (CRA) to vite-plugin-pwa.
 * Same SW URL (`/service-worker.js`) but different cache schemes; stale offline-plugin
 * caches can keep serving /static/* after deploy and break the Vite /assets/* build.
 *
 * Bumped to v2 so clients that completed v1 but remained on a mixed CRA/Vite cache
 * state get one more forced clear after the production launch edge cases.
 *
 * Returns true when a reload was triggered (caller should stop booting).
 */
export const VITE_SW_MIGRATION_STORAGE_KEY_V1 = 'guarnic:vite-sw-migrated-v1';
export const VITE_SW_MIGRATION_STORAGE_KEY = 'guarnic:vite-sw-migrated-v2';
export const VITE_SW_LEGACY_RECLEAR_ATTEMPTS_KEY = 'guarnic:vite-sw-legacy-reclear-attempts';
/** Max extra clear+reload passes when offline-plugin caches survive the first v2 clear. */
export const VITE_SW_LEGACY_RECLEAR_MAX_ATTEMPTS = 2;

const LEGACY_OFFLINE_CACHE_PATTERN = /webpack-offline|offline-plugin/i;

export const hasLegacyOfflinePluginCaches = async (
  cacheStorage = typeof caches !== 'undefined' ? caches : undefined,
) => {
  if (!cacheStorage?.keys) {
    return false;
  }

  const cacheKeys = await cacheStorage.keys();
  return cacheKeys.some(cacheKey => LEGACY_OFFLINE_CACHE_PATTERN.test(cacheKey));
};

export const migrateLegacyServiceWorkerIfNeeded = async ({
  reload = () => window.location.reload(),
  clearCaches = clearServiceWorkerAndCaches,
  hasLegacyCaches = hasLegacyOfflinePluginCaches,
} = {}) => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const alreadyMigrated =
      window.localStorage.getItem(VITE_SW_MIGRATION_STORAGE_KEY) === '1';
    const legacyCachesRemain = alreadyMigrated ? await hasLegacyCaches() : false;

    if (alreadyMigrated && !legacyCachesRemain) {
      window.localStorage.removeItem(VITE_SW_LEGACY_RECLEAR_ATTEMPTS_KEY);
      return false;
    }

    // Force CRA → Vite transition without the update modal: clear once (v2),
    // and re-clear a bounded number of times if offline-plugin caches remain.
    if (alreadyMigrated && legacyCachesRemain) {
      const attempts = Number(
        window.localStorage.getItem(VITE_SW_LEGACY_RECLEAR_ATTEMPTS_KEY) || '0',
      );
      if (!Number.isFinite(attempts) || attempts >= VITE_SW_LEGACY_RECLEAR_MAX_ATTEMPTS) {
        // eslint-disable-next-line no-console
        console.warn(
          '[vite-sw-migration] legacy caches remain after max re-clear attempts; booting anyway',
        );
        return false;
      }
      window.localStorage.setItem(VITE_SW_LEGACY_RECLEAR_ATTEMPTS_KEY, String(attempts + 1));
    } else {
      window.localStorage.removeItem(VITE_SW_LEGACY_RECLEAR_ATTEMPTS_KEY);
    }

    await clearCaches();

    // Only mark migrated after successful cleanup so a failed attempt can retry.
    window.localStorage.setItem(VITE_SW_MIGRATION_STORAGE_KEY, '1');
    // Drop the old v1 flag so a single source of truth remains.
    window.localStorage.removeItem(VITE_SW_MIGRATION_STORAGE_KEY_V1);
    reload();
    return true;
  } catch (error) {
    // Do not set the flag: next visit can retry unregister/cache clear.
    // No reload here, so this cannot loop the boot path.
    // eslint-disable-next-line no-console
    console.warn('[vite-sw-migration] cleanup failed', error);
    return false;
  }
};
